/**
 * Trunnion AI - Appearance Settings
 * Theme toggle, density, sidebar preferences.
 */

import { Moon, Sun } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { Card } from '@/components/ui/Card';

export function AppearanceSettings() {
  const { theme, setTheme, density, setDensity, sidebarCollapsed, setSidebarCollapsed } = useUIStore();

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-1">Theme</h3>
          <p className="text-sm text-trunnion-slate-400 mb-4">
            Choose your preferred color scheme.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {([
              { value: 'dark', label: 'Dark', icon: Moon, desc: 'Optimized for low-light environments' },
              { value: 'light', label: 'Light', icon: Sun, desc: 'Standard light theme' },
            ] as const).map(({ value, label, icon: Icon, desc }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={`flex flex-col items-center rounded-xl border p-4 transition-all ${
                  theme === value
                    ? 'border-trunnion-gold-500 bg-trunnion-gold-500/10'
                    : 'border-trunnion-slate-700 bg-trunnion-slate-800 hover:border-trunnion-slate-600'
                }`}
              >
                <Icon className={`h-6 w-6 mb-2 ${theme === value ? 'text-trunnion-gold-500' : 'text-trunnion-slate-400'}`} />
                <span className={`text-sm font-medium ${theme === value ? 'text-trunnion-gold-500' : 'text-white'}`}>
                  {label}
                </span>
                <span className="text-xs text-trunnion-slate-500 mt-1 text-center">{desc}</span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-1">Display Density</h3>
          <p className="text-sm text-trunnion-slate-400 mb-4">
            Control the spacing and size of UI elements.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {([
              { value: 'compact', label: 'Compact', desc: 'Maximum information density' },
              { value: 'comfortable', label: 'Comfortable', desc: 'Balanced spacing' },
              { value: 'spacious', label: 'Spacious', desc: 'More breathing room' },
            ] as const).map(({ value, label, desc }) => (
              <button
                key={value}
                onClick={() => setDensity(value)}
                className={`flex flex-col items-start rounded-xl border p-4 transition-all ${
                  density === value
                    ? 'border-trunnion-gold-500 bg-trunnion-gold-500/10'
                    : 'border-trunnion-slate-700 bg-trunnion-slate-800 hover:border-trunnion-slate-600'
                }`}
              >
                <span className={`text-sm font-medium ${density === value ? 'text-trunnion-gold-500' : 'text-white'}`}>
                  {label}
                </span>
                <span className="text-xs text-trunnion-slate-500 mt-1">{desc}</span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-1">Sidebar</h3>
          <p className="text-sm text-trunnion-slate-400 mb-4">
            Configure sidebar behavior.
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={sidebarCollapsed}
              onChange={(e) => setSidebarCollapsed(e.target.checked)}
              className="h-4 w-4 rounded border-trunnion-slate-600 bg-trunnion-slate-800 text-trunnion-gold-500 focus:ring-trunnion-gold-500"
            />
            <div>
              <span className="text-sm text-white">Start with sidebar collapsed</span>
              <p className="text-xs text-trunnion-slate-500">Sidebar will be minimized on load</p>
            </div>
          </label>
        </div>
      </Card>
    </div>
  );
}
